import { useEffect, useState } from "react";
import { useNotify } from "react-admin";
import { Alert, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from "@mui/material";
import Button from "@/components/ui/Button";
import { ConfirmationDialog } from "@/components/ui/ConfirmationDialog";
import { ButtonStyleType, CancelLabelType, ColorType, ConfirmLabelType } from "@/types/shared/uiComponents";
import { AlertType, type DateYMDString, type LaseredCondition } from "@/types";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { formatDate, todayLocalYMD } from "@/utils/formatters/dateFormatter";
import { parseRichError } from "@/utils/errors/richError";
import {
  defaultReviewBy,
  hasLiveExpectedTrigger,
  reviewByHasPassed,
  validateExpectedTrigger,
} from "@/utils/helpers/expectedTrigger";
import { laseredConditionApi } from "../hooks/useLaseredConditions";

const day = (d?: string) => (d ? formatDate(d.slice(0, 10)) : "—");
const ymd = (d?: string) => (d ? d.slice(0, 10) : "");
const money = (n?: number) =>
  n === undefined ? "—" : formatUSD(n, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

/** The API's message and its actionable detail, e.g. a 409 for a contract-lasered claimant. */
const errorText = (err: unknown): string => {
  const rich = parseRichError(err);
  return [rich.message, rich.details && rich.details !== rich.message ? rich.details : ""].filter(Boolean).join(" ");
};

const SAVES_NOW = "This saves now, not with the rest of the laser form.";

interface ExpectedTriggerDialogProps {
  open: boolean;
  claimantId: string;
  /** The saved condition (server state). */
  condition?: LaseredCondition;
  onClose: () => void;
  /** The condition as returned by the write. */
  onSaved: (saved: LaseredCondition) => void;
  /**
   * The server refused the action as stale (404, 409): the row changed under
   * this user. Reload it, so the same failing action is not offered again
   * (!4881 thread 30548).
   */
  onStale?: () => void;
}

const isStale = (err: unknown) => {
  const status = (err as { status?: number } | null)?.status;
  return status === 404 || status === 409;
};

/**
 * Set or edit a condition's expected trigger (#7361): the TPA reports a
 * qualifying claim that has not loaded yet. Expected date of service (inside
 * the incurred window, prefilled today), a reason and a review-by date
 * (prefilled 60 days later, capped at the paid window's end). Saved at once
 * with `PUT .../expected-trigger` after a confirm, because it changes the
 * deductible now.
 */
export const SetExpectedTriggerDialog = ({
  open,
  claimantId,
  condition,
  onClose,
  onSaved,
  onStale,
}: ExpectedTriggerDialogProps) => {
  const notify = useNotify();
  const [expectedDate, setExpectedDate] = useState("");
  const [reason, setReason] = useState("");
  const [reviewBy, setReviewBy] = useState("");
  const [reviewByTouched, setReviewByTouched] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState("");
  // Refused as stale: the reload below changes the condition under this dialog, so keep
  // the title it opened with and offer only Cancel (!4881 thread 30548).
  const [stale, setStale] = useState(false);
  const [openedEditing, setOpenedEditing] = useState(false);
  const live = hasLiveExpectedTrigger(condition);
  const editing = stale ? openedEditing : live;

  // Prefill once per open / condition: the live expected trigger when editing, else today.
  useEffect(() => {
    if (!open || !condition) return;
    const et = live ? condition.expected_trigger : undefined;
    const date = et ? ymd(et.expected_date) : todayLocalYMD();
    setExpectedDate(date);
    setReason(et?.reason ?? "");
    setReviewBy(et ? ymd(et.review_by) : defaultReviewBy(date, condition.paid_end_date));
    setReviewByTouched(!!et);
    setSubmitAttempted(false);
    setConfirmOpen(false);
    setServerError("");
    setStale(false);
    setOpenedEditing(live);
  }, [open, condition?.id]);

  if (!condition) return null;

  const errors = validateExpectedTrigger({ expected_date: expectedDate, reason, review_by: reviewBy }, condition);
  const valid = Object.keys(errors).length === 0;
  const name = condition.lasered_condition_name;
  const incurred = `${day(condition.incurred_start_date)} – ${day(condition.incurred_end_date)}`;
  const reviewByPassed = !errors.review_by && reviewByHasPassed(reviewBy);

  const changeExpectedDate = (value: string) => {
    setExpectedDate(value);
    // The review-by date follows the expected date until the user sets it.
    if (!reviewByTouched) setReviewBy(defaultReviewBy(value, condition.paid_end_date));
  };

  const handleContinue = () => {
    setSubmitAttempted(true);
    if (valid) setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    setSaving(true);
    try {
      const saved = await laseredConditionApi.setExpectedTrigger(claimantId, condition.id, {
        expected_date: expectedDate as DateYMDString,
        reason: reason.trim(),
        review_by: reviewBy as DateYMDString,
      });
      notify("Expected trigger saved. The deductible will refresh shortly.", { type: AlertType.SUCCESS });
      setConfirmOpen(false);
      onSaved(saved);
      onClose();
    } catch (err) {
      setConfirmOpen(false);
      setServerError(errorText(err) || "Could not save the expected trigger.");
      if (isStale(err)) {
        setStale(true);
        onStale?.();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={() => {
          if (!saving) onClose();
        }}
        fullWidth
        maxWidth="sm"
        aria-labelledby="expected-trigger-title"
      >
        <DialogTitle id="expected-trigger-title" variant="h3">
          {editing ? "Edit expected trigger" : "Set expected trigger"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" fontWeight={600}>
              {name} · {money(condition.amt_lasered_conditional_deductible)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Use this when the TPA reports a qualifying claim that has not loaded yet. Until the claim line arrives,
              the claimant&apos;s deductible follows this conditional laser from the expected date of service. The
              line&apos;s own date of service then replaces it.
            </Typography>
            {serverError && (
              <Alert severity="error" role="alert" data-testid="expected-trigger-error">
                {serverError}
              </Alert>
            )}
            <TextField
              label="Expected Date of Service"
              type="date"
              required
              fullWidth
              size="small"
              value={expectedDate}
              onChange={(e) => changeExpectedDate(e.target.value)}
              error={!!errors.expected_date}
              helperText={errors.expected_date ?? `Inside the incurred window, ${incurred}.`}
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: {
                  "data-testid": "expected-trigger-date",
                  min: ymd(condition.incurred_start_date),
                  max: ymd(condition.incurred_end_date),
                },
              }}
            />
            <TextField
              label="Reason"
              required
              fullWidth
              multiline
              minRows={2}
              size="small"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              error={submitAttempted && !!errors.reason}
              helperText={
                (submitAttempted && errors.reason) ||
                "e.g. TPA notice 10/07, claim in adjudication. Recorded with your name and the time."
              }
              slotProps={{ htmlInput: { "data-testid": "expected-trigger-reason" } }}
            />
            <TextField
              label="Review By"
              type="date"
              required
              fullWidth
              size="small"
              value={reviewBy}
              onChange={(e) => {
                setReviewBy(e.target.value);
                setReviewByTouched(true);
              }}
              error={!!errors.review_by}
              helperText={
                errors.review_by ??
                `If no qualifying claim line arrives by then, a deductible review opens. Defaults to 60 days after the expected date, no later than the paid window's end (${day(condition.paid_end_date)}).`
              }
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: {
                  "data-testid": "expected-trigger-review-by",
                  min: errors.expected_date ? undefined : expectedDate,
                  max: ymd(condition.paid_end_date),
                },
              }}
            />
            {reviewByPassed && (
              <Typography variant="caption" color="warning.main" data-testid="expected-trigger-review-by-passed">
                This review-by date has passed: a deductible review opens straight away.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant={ButtonStyleType.Text} color={ColorType.Inherit} onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant={ButtonStyleType.Contained}
            onClick={handleContinue}
            disabled={saving || stale || (submitAttempted && !valid)}
            data-testid="expected-trigger-continue"
          >
            {editing ? "Save expected trigger" : "Set expected trigger"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        open={open && confirmOpen}
        title="Change the deductible now?"
        message={`${SAVES_NOW} ${name} counts as triggered from ${day(expectedDate)}: the claimant's specific deductible is re-derived (the most recent date of service wins) and stop-loss claims are recalculated.`}
        confirmLabel={ConfirmLabelType.Confirm}
        cancelLabel={CancelLabelType.Back}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => void handleConfirm()}
        isLoading={saving}
      />
    </>
  );
};

/**
 * Cancel a condition's expected trigger (#7361 AC6), with a required reason.
 * Saved at once with `POST .../expected-trigger/cancel`; the deductible re-derives.
 */
export const CancelExpectedTriggerDialog = ({
  open,
  claimantId,
  condition,
  onClose,
  onSaved,
  onStale,
}: ExpectedTriggerDialogProps) => {
  const notify = useNotify();
  const [reason, setReason] = useState("");
  const [attempted, setAttempted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState("");
  const [stale, setStale] = useState(false);

  useEffect(() => {
    if (!open) return;
    setReason("");
    setAttempted(false);
    setServerError("");
    setStale(false);
  }, [open, condition?.id]);

  const missing = !reason.trim();

  const handleConfirm = async () => {
    setAttempted(true);
    if (!condition || missing) return;
    setSaving(true);
    try {
      const saved = await laseredConditionApi.cancelExpectedTrigger(claimantId, condition.id, {
        reason: reason.trim(),
      });
      notify("Expected trigger cancelled. The deductible will refresh shortly.", { type: AlertType.SUCCESS });
      onSaved(saved);
      onClose();
    } catch (err) {
      setServerError(errorText(err) || "Could not cancel the expected trigger.");
      if (isStale(err)) {
        setStale(true);
        onStale?.();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <ConfirmationDialog
      open={open && !!condition}
      title="Cancel the expected trigger?"
      message={`${SAVES_NOW} ${condition?.lasered_condition_name ?? "The conditional laser"} goes back to waiting for a qualifying claim line: the claimant's specific deductible is re-derived without it and stop-loss claims are recalculated.`}
      confirmLabel={ConfirmLabelType.Confirm}
      cancelLabel={CancelLabelType.Back}
      confirmColor={ColorType.Error}
      onCancel={onClose}
      onConfirm={() => void handleConfirm()}
      isLoading={saving}
      confirmDisabled={stale}
    >
      <Stack spacing={1.5} sx={{ mt: 2 }}>
        {serverError && (
          <Alert severity="error" role="alert" data-testid="cancel-expected-trigger-error">
            {serverError}
          </Alert>
        )}
        <TextField
          label="Reason for cancelling"
          required
          fullWidth
          multiline
          minRows={2}
          size="small"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          error={attempted && missing}
          helperText={attempted && missing ? "Enter a reason." : "Recorded with your name and the time."}
          slotProps={{ htmlInput: { "data-testid": "cancel-expected-trigger-reason" } }}
        />
      </Stack>
    </ConfirmationDialog>
  );
};
